import UiElement from '../../../shared/ui-element/ui-element';
import Toggle from '../toggle/toggle';

import defineAppDetailsElement from '../../components/app-details/app-details';

import './all-apps.scss';


export type App = {
  id: number;
  name: string;
  description: string;
  iconSrc: string;
  iconSrcSet: string;
  url: string;
  rating: number;
  ratingsAmount: number;
  platforms: string[];
  releaseDate: number;
  categories: string[];
  subcategories: number[];
  hidden?: boolean;
}

type SortOption = 'topRated' | 'new'
type PlatformFilterOption = 'mac' | 'ios_universal' | 'web'

const PLATFORM_MAPPER: { [keys in PlatformFilterOption]: string } = {
  mac: 'Mac',
  'ios_universal': 'iOS',
  web: 'Web',
};

class AllApps extends UiElement {
  apps: App[] = [];
  sortBy: SortOption = 'topRated';
  category?: string;
  platformsFilters = new Set<PlatformFilterOption>();
  subcategoriesFilters = new Set<number>();

  searchResultApps?: number[];

  ui = {
    'title': '.js-all-apps-title',
    'searchTitle': '.js-all-apps-search-title',
    'searchQuery': '.js-all-apps-search-query',
    'appsList': '.js-all-apps-list',
    'sortToggle': '.js-all-apps-sort-toggle',
    'filtersBar': '.js-all-apps-filters-bar',
  };

  uiCollections = {
    'sortOptions': '.js-all-apps-sort-toggle-option',
    'platformsTags': '.js-all-apps-platform-tag',
    'subcategoriesTags': '.js-all-apps-subcategory-tag',
    'appDetailsElements': 'app-details',
  };

  events = {
    'click @uiCollections.sortOptions': 'handleSortToggleOptionClick',
    'click @uiCollections.platformsTags': 'handlePlatformsTagClick',
    'click @uiCollections.subcategoriesTags': 'handleSubcategoriesTagClick',
  };

  tagActiveClass = 'tag_active';
  tagDisabledClass = 'tag_disabled';
  defaultTitleClass = 'js-all-apps-default-title';

  onInit() {
    super.onInit();

    defineAppDetailsElement();

    new Toggle({ rootElement: this.ui.sortToggle }).init();

    const appsList = (this.ui.appsList as unknown as HTMLElement);

    this.category = appsList.dataset.category;

    this.apps = JSON.parse(appsList.dataset.apps!);
    appsList.removeAttribute('data-apps');

    (this.uiCollections.subcategoriesTags as unknown as HTMLButtonElement[]).forEach((subcategory) => {
      if (subcategory.classList.contains(this.tagActiveClass)) {
        this.subcategoriesFilters.add(+subcategory.value);
      }
    });

    if (this.subcategoriesFilters.size) {
      this.filterApps();
    }
  }

  handleSortToggleOptionClick({ target: { value } }) {
    if (value === this.sortBy) return;

    this.sortBy = value;
    this.sortApps();
  }

  handlePlatformsTagClick({ target }) {
    if (target.classList.contains(this.tagDisabledClass)) return;

    target.classList.toggle(this.tagActiveClass);

    if (target.classList.contains(this.tagActiveClass)) {
      this.platformsFilters.add(target.value);
    } else {
      this.platformsFilters.delete(target.value);
    }

    this.filterApps();
    this.disableUnavailableSubcategoriesTags();
  }

  handleSubcategoriesTagClick({ target }) {
    if (target.classList.contains(this.tagDisabledClass)) return;

    target.classList.toggle(this.tagActiveClass);

    if (target.classList.contains(this.tagActiveClass)) {
      this.subcategoriesFilters.add(+target.value);
    } else {
      this.subcategoriesFilters.delete(+target.value);
    }

    this.filterApps();
    this.disableUnavailablePlatformsTags();
  }

  showSearchResult(appsIds, query) {
    this.searchResultApps = appsIds;
    const title = this.ui.title as unknown as HTMLElement;

    (this.ui.sortToggle as unknown as HTMLElement).classList.add('d-none');
    (this.ui.filtersBar as unknown as HTMLElement).classList.add('d-none');
    if (title.classList.contains(this.defaultTitleClass)) {
      title.classList.add('d-none');
    } else {
      title.classList.remove('d-sm-block');
    }
    (this.ui.searchTitle as unknown as HTMLElement).classList.remove('d-none');
    (this.ui.searchQuery as unknown as HTMLElement).textContent = query;
    this.filterApps();
    this.sortApps();
  }

  resetSearchResult() {
    this.searchResultApps = undefined;
    const title = this.ui.title as unknown as HTMLElement;

    (this.ui.sortToggle as unknown as HTMLElement).classList.remove('d-none');
    (this.ui.filtersBar as unknown as HTMLElement).classList.remove('d-none');
    if (title.classList.contains(this.defaultTitleClass)) {
      title.classList.remove('d-none');
    } else {
      title.classList.add('d-sm-block');
    }
    (this.ui.searchTitle as unknown as HTMLElement).classList.add('d-none');
    this.filterApps();
    this.sortApps();
  }

  sortApps() {
    this.apps.sort((app1, app2) => {
      if (this.searchResultApps) {
        const app1Index = this.searchResultApps.findIndex((id) => id === app1.id);
        const app2Index = this.searchResultApps.findIndex((id) => id === app2.id);

        return app1Index - app2Index;
      }

      if (this.sortBy === 'topRated') {
        return app2.rating - app1.rating
          || app2.ratingsAmount - app1.ratingsAmount
          || app1.name.localeCompare(app2.name);
      }

      return app2.releaseDate - app1.releaseDate;
    });

    (this.uiCollections.appDetailsElements as unknown as HTMLElement[]).forEach((appDetailsElement, orderIdx) => {
      const {
        name, description, iconSrc, iconSrcSet,
        url, rating, platforms, hidden,
      } = this.apps[orderIdx]!;

      appDetailsElement.classList.toggle('d-none', Boolean(hidden));

      if (appDetailsElement.getAttribute('name') === name) return;

      appDetailsElement.setAttribute('name', name);
      appDetailsElement.setAttribute('description', description);
      appDetailsElement.setAttribute('iconSrc', iconSrc);
      appDetailsElement.setAttribute('iconSrcSet', iconSrcSet);
      appDetailsElement.setAttribute('url', url);
      appDetailsElement.setAttribute('rating', String(rating));
      appDetailsElement.setAttribute('platforms', platforms.map((platform) => PLATFORM_MAPPER[platform]).join(', '));
    });
  }

  shouldAppBeHidden(app) {
    if (this.searchResultApps) return !this.searchResultApps.includes(app.id);

    if (this.category && !app.categories.includes(this.category)) return true;

    if (this.platformsFilters.size) {
      const hasAppSomePlatform = Array.from(this.platformsFilters).some((platform) => app.platforms.includes(platform));

      if (!hasAppSomePlatform) return true;
    }

    if (!this.subcategoriesFilters.size) return false;

    const doesAppBelongToSubcategories = Array.from(this.subcategoriesFilters).some(
      (subcategory) => app.subcategories.includes(subcategory)
    );

    return !doesAppBelongToSubcategories;
  }

  filterApps() {
    this.apps.forEach((app, appOrderIdx) => {
      app.hidden = this.shouldAppBeHidden(app);
      (this.uiCollections.appDetailsElements[appOrderIdx] as unknown as HTMLElement).classList.toggle('d-none', app.hidden);
    });
  }

  disableUnavailableSubcategoriesTags() {
    const subcategoriesTags = (this.uiCollections.subcategoriesTags as unknown as HTMLButtonElement[]);

    if (!subcategoriesTags.length) return;

    const platformsFilters = Array.from(this.platformsFilters);
    const availableSubcategories = new Set();
    this.apps
      .filter(({ platforms }) => {
        if (!platformsFilters.length) return true;

        return platformsFilters.some((platform) => platforms.includes(platform));
      })
      .forEach(({ subcategories }) => (
        subcategories.forEach((subcategory) => (
          availableSubcategories.add(subcategory))
        )
      ));

    subcategoriesTags.forEach((subcategoriesTag) => {
      if (
        subcategoriesTag.classList.contains(this.tagActiveClass)
        && !availableSubcategories.has(+subcategoriesTag.value)
      ) {
        subcategoriesTag.classList.remove(this.tagActiveClass);
        this.subcategoriesFilters.delete(+subcategoriesTag.value);
      }

      subcategoriesTag.classList.toggle(this.tagDisabledClass, !availableSubcategories.has(+subcategoriesTag.value));
    });
  }

  disableUnavailablePlatformsTags() {
    const platformsTags = (this.uiCollections.platformsTags as unknown as HTMLButtonElement[]);

    const subcategoriesFilters = Array.from(this.subcategoriesFilters);
    const availablePlatforms = new Set();
    this.apps
      .filter(({ subcategories }) => {
        if (!subcategoriesFilters.length) return true;

        return subcategoriesFilters.some((subcategory) => subcategories.includes(subcategory));
      })
      .forEach(({ platforms }) => (
        platforms.forEach((platform) => (
          availablePlatforms.add(platform))
        )
      ));

    platformsTags.forEach((platformTag) => {
      if (
        platformTag.classList.contains(this.tagActiveClass)
        && !availablePlatforms.has(platformTag.value)
      ) {
        platformTag.classList.remove(this.tagActiveClass);
        this.platformsFilters.delete(platformTag.value as PlatformFilterOption);
      }

      platformTag.classList.toggle(this.tagDisabledClass, !availablePlatforms.has(platformTag.value));
    });
  }
}

export default AllApps as UiElement;
